import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, BarChart3, Clock, CalendarDays, Gift, AlertTriangle } from 'lucide-react';
import { SearchInput } from '@/components/ui/search-input';
import { attendanceService, type DailyRecordWithUser } from '@/services/attendance.service';

// ── Types ──────────────────────────────────────────────────────────────────────

interface UserMonthStats {
  user_id: string;
  first_name: string | null;
  last_name: string | null;
  employee_code: string | null;
  photo_url: string | null;
  present: number;
  late: number;
  half_day: number;
  absent: number;
  leave: number;
  wfh: number;
  total_working_minutes: number;
  break_minutes: number;
  total_days: number;
  paid_hours_approved: number;
  paid_hours_pending: number;
  penalty_approved_minutes: number;
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function pad(n: number) { return String(n).padStart(2, '0'); }

function getFirstDay(year: number, month: number): string {
  return `${year}-${pad(month + 1)}-01`;
}

function getLastDay(year: number, month: number): string {
  const d = new Date(year, month + 1, 0);
  return `${year}-${pad(month + 1)}-${pad(d.getDate())}`;
}

function getMonthLabel(year: number, month: number): string {
  return new Date(year, month, 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
}

function formatHours(mins: number): string {
  if (!mins || mins <= 0) return '0h 0m';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function getInitials(first: string | null, last: string | null): string {
  const f = first?.trim()[0] ?? '';
  const l = last?.trim()[0] ?? '';
  return (f + l).toUpperCase() || '?';
}

function attendancePct(s: UserMonthStats): number {
  if (!s.total_days) return 0;
  const effective = s.present + s.late + s.wfh + s.half_day * 0.5;
  return Math.min(100, Math.round((effective / s.total_days) * 100));
}

function groupByUser(
  records: DailyRecordWithUser[],
  dateFrom: string,
  dateTo: string,
): UserMonthStats[] {
  const map = new Map<string, UserMonthStats>();
  const today = new Date().toISOString().slice(0, 10);

  for (const r of records) {
    let entry = map.get(r.user_id);
    if (!entry) {
      entry = {
        user_id: r.user_id,
        first_name: r.first_name,
        last_name: r.last_name,
        employee_code: r.employee_code,
        photo_url: r.photo_url,
        present: 0, late: 0, half_day: 0, absent: 0, leave: 0, wfh: 0,
        total_working_minutes: 0,
        break_minutes: 0,
        total_days: 0,
        paid_hours_approved: 0,
        paid_hours_pending: 0,
        penalty_approved_minutes: 0,
      };
      map.set(r.user_id, entry);
    }

    entry.total_days++;
    entry.total_working_minutes += r.total_working_minutes ?? 0;
    entry.break_minutes += r.break_minutes ?? 0;
    entry.paid_hours_approved        += Number(r.paid_hours_approved        ?? 0);
    entry.paid_hours_pending         += Number(r.paid_hours_pending         ?? 0);
    entry.penalty_approved_minutes   += Number(r.penalty_approved_minutes   ?? 0);

    switch (r.status) {
      case 'present':        entry.present++;  break;
      case 'late':           entry.late++;     break;
      case 'half_day':       entry.half_day++; break;
      case 'absent':         entry.absent++;   break;
      case 'leave':          entry.leave++;    break;
      case 'work_from_home': entry.wfh++;      break;
    }
  }

  // Add implicit absents: Mon–Sat working days before today with no record.
  // daily_attendance_records only gets a row when punches exist, so missing days
  // are absent but invisible to the record-only loop above.
  for (const [userId, entry] of map.entries()) {
    const recordedDates = new Set(
      records
        .filter((r) => r.user_id === userId)
        .map((r) => r.attendance_date.slice(0, 10)),
    );

    const cursor = new Date(dateFrom + 'T00:00:00');
    const end    = new Date(dateTo   + 'T00:00:00');
    while (cursor <= end) {
      const ds = cursor.toISOString().slice(0, 10);
      if (ds >= today) break; // skip today and future
      const dow = cursor.getDay();
      if (dow !== 0 && !recordedDates.has(ds)) {
        // Mon–Sat working day with no punch record → absent
        entry.absent++;
        entry.total_days++;
      }
      cursor.setDate(cursor.getDate() + 1);
    }
  }

  return Array.from(map.values()).sort((a, b) => {
    const nameA = [a.first_name, a.last_name].filter(Boolean).join(' ').toLowerCase();
    const nameB = [b.first_name, b.last_name].filter(Boolean).join(' ').toLowerCase();
    return nameA.localeCompare(nameB);
  });
}

// ── Employee month card ────────────────────────────────────────────────────────

function EmployeeMonthCard({ stats, onCardClick }: { stats: UserMonthStats; onCardClick: (userId: string) => void }) {
  const fullName = [stats.first_name, stats.last_name].filter(Boolean).join(' ') || '–';
  const pct = attendancePct(stats);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onCardClick(stats.user_id)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onCardClick(stats.user_id); }}
      className="w-full min-w-0 overflow-hidden cursor-pointer rounded-xl border border-slate-200/80 dark:border-border bg-white dark:bg-card shadow-sm hover:shadow-md hover:border-teal-300 dark:hover:border-teal-600 transition-all"
    >
      {/* Top row: avatar + name + pct */}
      <div className="flex items-start gap-3 px-4 pt-4 pb-3">
        {/* Avatar */}
        <div className="h-11 w-11 shrink-0 overflow-hidden rounded-full border border-slate-200 dark:border-border">
          {stats.photo_url ? (
            <img src={stats.photo_url} alt={fullName} className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-teal-600 text-xs font-bold text-white">
              {getInitials(stats.first_name, stats.last_name)}
            </div>
          )}
        </div>

        {/* Name + code */}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold uppercase tracking-wide text-slate-800 dark:text-slate-100 truncate">{fullName}</p>
          <p className="text-xs text-slate-400 dark:text-slate-500">ID: {stats.employee_code ?? '–'}</p>
        </div>

        {/* Attendance % badge */}
        <div className="shrink-0 text-right">
          <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">Attendance</span>
          <p className="text-sm font-bold text-teal-600 dark:text-teal-400">{pct}%</p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="mx-4 mb-3 h-1.5 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700">
        <div
          className="h-full rounded-full bg-teal-600 transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>

      {/* Stat boxes */}
      <div className="mx-4 mb-3 grid grid-cols-3 gap-2">
        <div className="flex flex-col items-center rounded-lg bg-emerald-50 dark:bg-emerald-900/20 py-2.5">
          <span className="text-lg font-bold text-emerald-600 dark:text-emerald-400 leading-none">{stats.present + stats.late}</span>
          <span className="mt-0.5 text-[10px] font-medium text-emerald-500 dark:text-emerald-500 uppercase tracking-wide">Present</span>
        </div>
        <div className="flex flex-col items-center rounded-lg bg-red-50 dark:bg-red-900/20 py-2.5">
          <span className="text-lg font-bold text-red-500 dark:text-red-400 leading-none">{stats.absent}</span>
          <span className="mt-0.5 text-[10px] font-medium text-red-400 dark:text-red-500 uppercase tracking-wide">Absent</span>
        </div>
        <div className="flex flex-col items-center rounded-lg bg-amber-50 dark:bg-amber-900/20 py-2.5">
          <span className="text-lg font-bold text-amber-500 dark:text-amber-400 leading-none">{stats.half_day}</span>
          <span className="mt-0.5 text-[10px] font-medium text-amber-400 dark:text-amber-500 uppercase tracking-wide">Half Day</span>
        </div>
      </div>

      {/* Working + Break row */}
      <div className="flex items-center justify-between border-t border-slate-100 dark:border-border/60 px-4 py-2.5 text-xs">
        <div className="flex items-center gap-1.5 text-slate-500 dark:text-slate-400">
          <Clock className="h-3.5 w-3.5 text-teal-500" />
          <span>Working</span>
          <span className="font-semibold text-teal-600 dark:text-teal-400">{formatHours(stats.total_working_minutes)}</span>
        </div>
        <div className="flex items-center gap-1.5 text-slate-500 dark:text-slate-400">
          <span>Break</span>
          <span className="font-semibold text-violet-500 dark:text-violet-400">{formatHours(stats.break_minutes)}</span>
        </div>
      </div>

      {/* Leave row — only if has leave days */}
      {stats.leave > 0 && (
        <div className="flex items-center justify-between border-t border-slate-100 dark:border-border/60 px-4 py-2 text-xs">
          <span className="text-slate-500 dark:text-slate-400">Paid Leave</span>
          <span className="inline-flex items-center rounded-full bg-purple-100 dark:bg-purple-900/40 px-2.5 py-0.5 text-[11px] font-semibold text-purple-600 dark:text-purple-300">
            +{formatHours(stats.leave * 8 * 60)}
          </span>
        </div>
      )}

      {/* WFH row — only if has wfh days */}
      {stats.wfh > 0 && (
        <div className="flex items-center justify-between border-t border-slate-100 dark:border-border/60 px-4 py-2 text-xs">
          <span className="text-slate-500 dark:text-slate-400">Work From Home</span>
          <span className="inline-flex items-center rounded-full bg-teal-100 dark:bg-teal-900/40 px-2.5 py-0.5 text-[11px] font-semibold text-teal-600 dark:text-teal-300">
            {stats.wfh} {stats.wfh === 1 ? 'day' : 'days'}
          </span>
        </div>
      )}

      {/* Penalty row — only if approved penalties exist */}
      {stats.penalty_approved_minutes > 0 && (
        <div className="flex items-center justify-between border-t border-slate-100 dark:border-border/60 px-4 py-2 text-xs">
          <div className="flex items-center gap-1 text-slate-500 dark:text-slate-400">
            <AlertTriangle className="h-3 w-3 text-rose-500" />
            <span>Penalty</span>
          </div>
          <span className="inline-flex items-center rounded-full bg-rose-100 dark:bg-rose-900/40 px-2.5 py-0.5 text-[11px] font-semibold text-rose-600 dark:text-rose-300">
            -{formatHours(stats.penalty_approved_minutes)}
          </span>
        </div>
      )}

      {/* Company Paid Hours row — only if any paid hours exist */}
      {(stats.paid_hours_approved > 0 || stats.paid_hours_pending > 0) && (
        <div className="flex items-center justify-between border-t border-slate-100 dark:border-border/60 px-4 py-2 text-xs">
          <div className="flex items-center gap-1 text-slate-500 dark:text-slate-400">
            <Gift className="h-3 w-3 text-teal-500" />
            <span>Company Paid</span>
          </div>
          <div className="flex items-center gap-1.5">
            {stats.paid_hours_approved > 0 && (
              <span className="inline-flex items-center rounded-full bg-teal-100 dark:bg-teal-900/40 px-2.5 py-0.5 text-[11px] font-semibold text-teal-600 dark:text-teal-300">
                +{stats.paid_hours_approved}h ✓
              </span>
            )}
            {stats.paid_hours_pending > 0 && (
              <span className="inline-flex items-center rounded-full bg-amber-100 dark:bg-amber-900/40 px-2.5 py-0.5 text-[11px] font-semibold text-amber-600 dark:text-amber-300">
                {stats.paid_hours_pending}h pending
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

interface Props {
  selectedUserId: string;
}

export function MonthlyOverviewTab({ selectedUserId }: Props) {
  const navigate = useNavigate();
  const [month, setMonth] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });
  const [search, setSearch] = useState('');

  const dateFrom = getFirstDay(month.year, month.month);
  const dateTo   = getLastDay(month.year, month.month);

  const { data, isLoading } = useQuery({
    queryKey: ['attendance', 'monthly-overview', month.year, month.month],
    queryFn: () =>
      attendanceService.listDaily({ date_from: dateFrom, date_to: dateTo, limit: 1000 }),
  });

  const userStats = useMemo(
    () => groupByUser(data?.data ?? [], dateFrom, dateTo),
    [data, dateFrom, dateTo],
  );

  const filtered = useMemo(() => {
    let result = selectedUserId
      ? userStats.filter((u) => u.user_id === selectedUserId)
      : userStats;
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter((u) =>
        [u.first_name, u.last_name, u.employee_code].filter(Boolean).join(' ').toLowerCase().includes(q),
      );
    }
    return result;
  }, [userStats, selectedUserId, search]);

  const prevMonth = () =>
    setMonth(({ year, month: m }) =>
      m === 0 ? { year: year - 1, month: 11 } : { year, month: m - 1 },
    );

  const nextMonth = () =>
    setMonth(({ year, month: m }) =>
      m === 11 ? { year: year + 1, month: 0 } : { year, month: m + 1 },
    );

  return (
    <div className="w-full min-w-0 space-y-3">
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <CalendarDays className="h-4 w-4 text-teal-600" />
          <div>
            <h2 className="text-base font-bold text-slate-800 dark:text-slate-100">Monthly Overview</h2>
            {!isLoading && (
              <p className="text-xs text-slate-400 dark:text-slate-500">{userStats.length} employees</p>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Month navigator */}
          <div className="flex items-center rounded-lg border border-slate-200 dark:border-border bg-white dark:bg-card">
            <button
              onClick={prevMonth}
              className="rounded-l-lg p-2 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-muted transition-colors"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="min-w-[110px] px-1 text-center text-sm font-semibold text-slate-700 dark:text-slate-200">
              {getMonthLabel(month.year, month.month)}
            </span>
            <button
              onClick={nextMonth}
              className="rounded-r-lg p-2 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-muted transition-colors"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

        </div>
      </div>

      {/* ── Search ─────────────────────────────────────────────────────────── */}
      <SearchInput
        value={search}
        onChange={setSearch}
        placeholder="Search by name or code…"
        containerClassName="w-full sm:max-w-sm"
      />

      {/* ── Cards ──────────────────────────────────────────────────────────── */}
      {isLoading ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-48 animate-pulse rounded-xl border border-slate-200 dark:border-border bg-slate-50 dark:bg-muted" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 dark:border-border bg-white dark:bg-card py-16 text-center">
          <BarChart3 className="mb-2 h-8 w-8 text-slate-300 dark:text-slate-600" />
          <p className="text-sm text-slate-500 dark:text-slate-400">No attendance records found for this month</p>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((stats) => (
            <EmployeeMonthCard
              key={stats.user_id}
              stats={stats}
              onCardClick={(id) => navigate(`/hrms/staff/${id}`)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
